import { ApolloError, useMutation } from "@apollo/client";
import * as React from "react";
import { useRef, useState } from "react";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ExtendTicket, PartialSyncSnapshot, TicketExtended } from "../Utils/TypeExtensions";
import { Ticket } from "../sharedCode/avroGraphql";
import { CREATE_OR_UPDATE_TICKET } from "../sharedCode/queries";
import { logSentry } from "../Utils/Sentry";
import TicketValidator, { DUPLICATE_TICKET, FAILED_SYNC, PARTIAL_SYNC } from "../Utils/TicketValidator";
import { BackgroundProps, ErrorLevel, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { ApolloErrorParser, TICKET_ALREADY_EXISTS } from "../Utils/ApolloErrorParser";
import { validateServerResponse } from "../Utils/TicketComparer";
import { mergeLocalTicketWithServerResult } from "../Utils/TicketMerger";
import { isLastRevision } from "../sharedCode/HelperMethods";

const PAYLOAD_CHECKSUM_HEADER = "x-ticket-payload-sha256";

async function sha256Hex(value: string): Promise<string> {
    const data = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

/** Local-only fields for a successful push response; clear partialSyncSnapshot now that sync matched. */
function preserveLocalTicketFieldsOnSuccess(
    localTicket: TicketExtended
): Pick<TicketExtended, "hasLocalChanges" | "nextLocalId" | "OpenedCards" | "pushAttempts" | "isDemoTicket" | "isDuplicateTicket" | "partialSyncSnapshot"> {
    return {
        hasLocalChanges: false,
        nextLocalId: localTicket.nextLocalId,
        OpenedCards: localTicket.OpenedCards ?? [],
        pushAttempts: 0,
        isDemoTicket: localTicket.isDemoTicket ?? false,
        isDuplicateTicket: localTicket.isDuplicateTicket ?? false,
        partialSyncSnapshot: undefined,
    };
}

/** Local-only fields to preserve on failure (server never returns these; keep pushAttempts from server stub). */
function preserveLocalTicketFieldsOnFailure(
    localTicket: TicketExtended
): Pick<TicketExtended, "nextLocalId" | "OpenedCards" | "isDemoTicket" | "isDuplicateTicket" | "partialSyncSnapshot"> {
    return {
        nextLocalId: localTicket.nextLocalId,
        OpenedCards: localTicket.OpenedCards ?? [],
        isDemoTicket: localTicket.isDemoTicket ?? false,
        isDuplicateTicket: localTicket.isDuplicateTicket ?? false,
        partialSyncSnapshot: localTicket.partialSyncSnapshot,
    };
}

/** Returns true if the ticket in DB appears unchanged from the snapshot we pushed (list lengths and nextLocalId). */
function ticketUnchangedSincePush(localTicket: TicketExtended, currentInDb: TicketExtended | undefined): boolean {
    if (!currentInDb) return false;
    return (
        (localTicket.LineItems?.length ?? 0) === (currentInDb.LineItems?.length ?? 0) &&
        (localTicket.Locations?.length ?? 0) === (currentInDb.Locations?.length ?? 0) &&
        (localTicket.Pax?.length ?? 0) === (currentInDb.Pax?.length ?? 0) &&
        (localTicket.Signatures?.length ?? 0) === (currentInDb.Signatures?.length ?? 0) &&
        localTicket.nextLocalId === currentInDb.nextLocalId
    );
}

function snapshotTicketForAudit(ticket: TicketExtended): Record<string, unknown> {
    const snapshot = JSON.parse(JSON.stringify(ticket)) as Record<string, unknown>;
    delete snapshot.partialSyncSnapshot;
    return snapshot;
}

function buildPartialSyncSnapshot(localTicket: TicketExtended, serverTicket: TicketExtended): PartialSyncSnapshot {
    return {
        timestamp: new Date().toISOString(),
        originalTicket: snapshotTicketForAudit(localTicket),
        serverResponse: snapshotTicketForAudit(serverTicket),
    };
}

function getPartialSyncSnapshotToSave(localTicket: TicketExtended, serverTicket: TicketExtended): PartialSyncSnapshot {
    return localTicket.partialSyncSnapshot ?? buildPartialSyncSnapshot(localTicket, serverTicket);
}

function formatApolloSyncError(error: ApolloError): string {
    const graphQLErrorMessages = error.graphQLErrors.map((gqlError) => gqlError.message).join(" || ");
    const networkErrorMessage = error.networkError?.message ?? "";
    const rawErrorMessage = error.message ?? "";
    return [rawErrorMessage, graphQLErrorMessages, networkErrorMessage].filter((m) => m.length > 0).join(" | ");
}

function localPropertyRemover(key: string, value: any) {
    // this only modifies the json string as it's being created, there's nothing to roll back if the sync fails

    // TODO iterate through the TicketExtended members instead of hardcoding (actually, I think gql is just ignoring extra props, do we need to bother doing this at all?)
    // don't send any of the extended properties
    const fieldsToRemove = [
        "hasLocalChanges",
        "nextLocalId",
        "Company",
        "Pilot",
        "Pilot2",
        "AME",
        "Aircraft",
        "isDemoTicket",
        "keyHash",
        "valueHash",
        "selectName",
        "partialSyncSnapshot",
    ];
    if (fieldsToRemove.findIndex((unwantedFieldName) => unwantedFieldName === key) >= 0) {
        return undefined;
    }

    // set any negative id fields to null since they're local ID's and will cause an error if we send them
    if (key === "id" && typeof value === "number" && value < 0) {
        return undefined;
    }

    return value;
}

export default function useTicketBackgroundPush(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Fresh);
    const [pushTicket] = useMutation(CREATE_OR_UPDATE_TICKET, { fetchPolicy: "no-cache" });
    const pushInProgressRef = useRef(false);

    // TODO controlling the useEffect with this const causes it to run once and kick off the pollForChanges loop... there's probably a more elegant way to do this
    const useEffectAnchor = "TODO";

    function pushTicketsToServer(ticketsToUpdate: Array<TicketExtended>) {
        return ticketsToUpdate.map(async (unsyncedTicket) => {
            try {
                const jsonToPush = JSON.stringify({ ticket: unsyncedTicket }, localPropertyRemover);
                const payloadChecksum = await sha256Hex(jsonToPush);

                // let jsonToPush: string;
                // if (unsyncedTicket.id < 0) {
                //     const partialTicket = JSON.parse(JSON.stringify({ ...unsyncedTicket })) as TicketExtended;
                //     partialTicket.LineItems = DefinitelyAnArray(partialTicket.LineItems).filter((i) => (i?.line_item_type_id ?? 99) < 6);
                //     // partialTicket.LineItems = partialTicket && partialTicket.LineItems && partialTicket.LineItems.length > 0 ?  [partialTicket?.LineItems[0]] : [];
                //     // partialTicket.Locations = partialTicket && partialTicket.Locations && partialTicket.Locations.length > 0 ?  [partialTicket?.Locations[0]] : [];
                //     // partialTicket.Pax = partialTicket && partialTicket.Pax && partialTicket.Pax.length > 0 ?  [partialTicket?.Pax[0]] : [];
                //     // partialTicket.Signatures = partialTicket && partialTicket.Signatures && partialTicket.Signatures.length > 0 ?  [partialTicket?.Signatures[0]] : [];
                //
                //     jsonToPush = JSON.stringify({ ticket: partialTicket }, localPropertyRemover);
                // } else {
                //     jsonToPush = JSON.stringify({ ticket: unsyncedTicket }, localPropertyRemover);
                // }

                // throw new ApolloError({ errorMessage: "push failed" });
                const serverResponse = await pushTicket({
                    variables: {
                        ticket: jsonToPush,
                    },
                    context: {
                        headers: {
                            [PAYLOAD_CHECKSUM_HEADER]: payloadChecksum,
                        },
                    },
                });

                // TEST: Add passenger "OOPS" so saveResponsesToLocalDb sees ticket modified during push → PUSH_SKIP_OVERWRITE
                // const hasOops = (unsyncedTicket.Pax ?? []).some((p) => p?.name === "OOPS");
                // if (!hasOops) {
                //     const oopsPax = {
                //         id: unsyncedTicket.nextLocalId ?? 0,
                //         name: "OOPS",
                //         ticket_id: unsyncedTicket.id,
                //     } as TicketPax;
                //     await addPassenger(unsyncedTicket.id, oopsPax);
                // }

                return {
                    localTicket: unsyncedTicket,
                    serverTicket: ExtendTicket(serverResponse.data.createOrUpdateTicket as Ticket),
                    success: true,
                };
            } catch (e) {
                if (e instanceof ApolloError) {
                    if (ApolloErrorParser.parse(e.message) == TICKET_ALREADY_EXISTS) {
                        setStatus(SyncStatus.Stale);
                        logSentry("DUPLICATE_TICKET", unsyncedTicket.ticket_num);

                        return {
                            localTicket: unsyncedTicket,
                            serverTicket: {
                                ...unsyncedTicket,
                                isDuplicateTicket: true,
                                pushAttempts: DUPLICATE_TICKET,
                            } as TicketExtended,
                            success: false,
                        };
                    } else {
                        const errorDetails = formatApolloSyncError(e);
                        logSentry("FATAL_SYNC_ERROR", `${unsyncedTicket.ticket_num} | ${errorDetails}`);

                        setStatus(SyncStatus.Stale);

                        return {
                            localTicket: unsyncedTicket,
                            serverTicket: {
                                ...unsyncedTicket,
                                pushAttempts: FAILED_SYNC,
                            } as TicketExtended,
                            success: false,
                        };
                    }
                } else {
                    throw e;
                }
            }
        });
    }

    function saveResponsesToLocalDb(
        serverResponses: {
            success: boolean;
            localTicket: TicketExtended;
            serverTicket: TicketExtended;
        }[]
    ) {
        return serverResponses.map(async (pushResult) => {
            try {
                return await TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
                    // push was completely rejected, just save the ticket with its new status and bail out (preserve local-only fields)
                    if (!pushResult.success) {
                        await TicketDatabase.ticket.put({ ...pushResult.serverTicket, ...preserveLocalTicketFieldsOnFailure(pushResult.localTicket) }, pushResult.localTicket.id);
                        return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
                    }

                    const localServerDifferences = validateServerResponse(pushResult.localTicket, pushResult.serverTicket);

                    if (Object.keys(localServerDifferences).length === 0) {
                        // the server returned all the data we sent and nothing more, all is well
                        // Re-read the ticket from DB: if it was modified during push (e.g. user saved), do not overwrite to avoid data loss
                        const currentInDb = await TicketDatabase.ticket.get(pushResult.localTicket.id);
                        if (!ticketUnchangedSincePush(pushResult.localTicket, currentInDb)) {
                            // NB if this path is taken none of the server data will be saved locally
                            // about the only way to deal with it if it ever happens would be to delete the ticket from RPS and re-sync
                            logSentry("PUSH_SKIP_OVERWRITE", `${pushResult.localTicket.ticket_num} was modified during push; leaving hasLocalChanges true`);
                            return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
                        }
                        const toSave = {
                            ...pushResult.serverTicket,
                            ...preserveLocalTicketFieldsOnSuccess(pushResult.localTicket),
                        };
                        // When the server assigns a new id (e.g. first push), re-key the record: delete old, put new
                        if (pushResult.serverTicket.id !== pushResult.localTicket.id) {
                            await TicketDatabase.ticket.delete(pushResult.localTicket.id);
                            await TicketDatabase.ticket.put(toSave);
                        } else {
                            const updateCount = await TicketDatabase.ticket.update(pushResult.localTicket.id, toSave);
                            if (updateCount !== 1) {
                                return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
                            }
                        }
                        return { ticketNumber: pushResult.localTicket.ticket_num, success: true };
                    } else {
                        // Server returned different data; merge so local data gets server-assigned IDs, then save
                        logSentry("PARTIAL_SYNC", `${pushResult.localTicket.ticket_num} | ${JSON.stringify(localServerDifferences)}`);
                        const merged = mergeLocalTicketWithServerResult(pushResult.localTicket, pushResult.serverTicket);
                        const partialSyncSnapshot = getPartialSyncSnapshotToSave(pushResult.localTicket, pushResult.serverTicket);
                        const toSave = {
                            ...merged,
                            ...preserveLocalTicketFieldsOnSuccess(pushResult.localTicket),
                            pushAttempts: PARTIAL_SYNC,
                            hasLocalChanges: true,
                            partialSyncSnapshot,
                        };
                        if (pushResult.serverTicket.id !== pushResult.localTicket.id) {
                            await TicketDatabase.ticket.delete(pushResult.localTicket.id);
                            await TicketDatabase.ticket.put(toSave);
                        } else {
                            await TicketDatabase.ticket.update(pushResult.localTicket.id, toSave);
                        }
                        setStatus(SyncStatus.Error);
                        return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
                    }
                });
            } catch (e: any) {
                logSentry("SAVE_RESPONSE_FAILURE", e);
                return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
            }
        });
    }

    const fetchData = async () => {
        if (pushInProgressRef.current) return;
        pushInProgressRef.current = true;
        setStatus(SyncStatus.Loading);

        try {
            const aircraftList = await TicketDatabase.aircraft.toArray();
            const freshTicketList = await TicketDatabase.ticket.toArray();
            const ticketsToUpdate = await TicketDatabase.ticket
                .filter(
                    (ticket) =>
                        !(ticket.isDemoTicket === undefined ? false : ticket.isDemoTicket) &&
                        (!TicketValidator.IsStale(ticket) ||
                            (ticket.id < 0 && TicketValidator.IsStale(ticket) && ticket.pushAttempts !== FAILED_SYNC && ticket.pushAttempts !== PARTIAL_SYNC)) &&
                        ticket.hasLocalChanges &&
                        TicketValidator.IsReadOnly(ticket) &&
                        isLastRevision(freshTicketList, ticket) &&
                        TicketValidator.validate(aircraftList, freshTicketList, ticket).filter((vr) => vr.level === ErrorLevel.Error).length === 0 &&
                        ticket.pushAttempts === 0
                )
                .toArray();

            const serverResponses = await Promise.all(pushTicketsToServer(ticketsToUpdate));

            await Promise.all(saveResponsesToLocalDb(serverResponses));

            // if any syncs failed status will already be set to stale
            setStatus((prev) => {
                return prev === SyncStatus.Loading ? SyncStatus.Fresh : prev;
            });
        } finally {
            pushInProgressRef.current = false;
        }
    };

    return { status, fetchData: fetchData };
}
